<template>
  <section v-if="lastClips">
    <div class="clips-title">
      <video-icon/>
      <span>{{ $t('CLIP.gameClips') }}</span>
      <button-component
        variant="icon"
        class="refresh-button"
        @btn-click="refreshClips"
        title="Reload"
        :class="{rotate: loading}"
      >
        <refresh-icon/>
      </button-component>
    </div>
    <div class="clips-table">
      <div class="clip-items-title">
        <span>{{ $t('Start time') }}</span>
        <span>{{ $t('Name') }}</span>
        <span>{{ $t('Duration') }}</span>
        <div/>
      </div>
      <created-clip-item
        v-for="clip in lastClips"
        :clip="clip"
        :key="clip.id"
        @delete="onDelete"
        @playClip="onPlayClip"
        :disabled="processingIds.includes(clip.id)"
      />
    </div>
    <div class="mt-4">
      <app-link
        :to="CLIPS"
        class="clip_button"
      >
        {{ $t('Clips') }}
      </app-link>
    </div>
  </section>
</template>

<script>
import {mapActions, mapMutations, mapState} from 'vuex';
import RefreshIcon from '@/assets/img/RefreshIcon';
import {
  ADD_POPUP,
  DELETE_CLIP,
  DOWNLOAD_CLIP,
  GET_ALL_CLIPS_FOR_ID,
} from '@/store/actionTypes';
import ButtonComponent from '@/components/base-components/buttons/ButtonComponent';
import CreatedClipItem from './CreatedClipItem';
import VideoIcon from '@/assets/img/VideoIcon';
import {ADD_TOAST} from '@/store/mutationTypes';
import {CLIP_STATUSES} from '@/const';
import AppLink from '@/components/base-components/AppLink.vue';
import {CLIPS} from '@/pages';

export default {
  name: 'CreatedClips',
  components: {
    AppLink,
    VideoIcon,
    CreatedClipItem,
    ButtonComponent,
    RefreshIcon,
  },
  props: {
    retryInterval: {
      type: Number,
      default: 10000,
    },
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      processingIds: [],
      timeoutId: null,
    };
  },
  computed: {
    CLIPS() {
      return CLIPS;
    },
    ...mapState({
      clips: (state) => state.clips.clips,
    }),
    lastClips() {
      if (this.clips) {
        const limit = 3;
        return [...this.clips].sort((a, b) => b.id - a.id).slice(0, limit);
      }
      return [];
    },
    hasProcessing() {
      return this.lastClips.some(
          ({status}) => status === CLIP_STATUSES.processing,
      );
    },
  },
  watch: {
    lastClips() {
      if (this.hasProcessing) {
        this.timeoutId = setTimeout(
            this.refreshClips,
            this.retryInterval,
        );
      }
    },
  },
  methods: {
    ...mapActions({
      getClips: GET_ALL_CLIPS_FOR_ID,
      deleteClipRequest: DELETE_CLIP,
      getClip: DOWNLOAD_CLIP,
      addPopup: ADD_POPUP,
    }),
    ...mapMutations({
      toast: ADD_TOAST,
    }),
    async refreshClips() {
      this.loading = true;
      await this.getClips({id: this.event.id}).finally(() => {
        this.loading = false;
      });
    },
    onDelete(clip) {
      this.processingIds.push(clip.id);
      const promise = this.addPopup({
        component: './DeleteClipPopup.vue',
        clip: {
          ...clip,
          event: this.event,
        },
      });
      promise.then(async () => {
        await this.deleteClipRequest({
          id: clip.id,
        });
        await this.refreshClips();
      })
          .catch(() => {})
          .finally(async () => {
            this.processingIds = this.processingIds.filter((exist) => exist !== clip.id);
          });
    },
    onPlayClip(times) {
      this.$emit('playClip', times);
    },
  },
  mounted() {
    this.refreshClips();
  },
  beforeDestroy() {
    clearInterval(this.timeoutId);
  },
};
</script>

<style scoped lang="scss">
@import 'src/assets/css/mixins';

.rotate {
  animation: rotate 0.75s linear infinite;
}

.clips-title {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1rem;
}

.clips-table {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 7.5ch 1fr 7ch minmax(4rem, 7.5rem);
  align-items: center;
}

.clip-items-title {
  display: contents;
  font-size: 0.75rem;
  span {
    text-align: start;
    align-self: flex-end;
    @include media-max(sm) {
      white-space: normal;
    }
  }
}

.created-clips .refresh-button {
  svg {
    height: 1rem;
    max-height: 1rem; // ios bug fix
  }
}

.clip_button {
  --background: var(--theme-link-accent);
  --text: var(--settings-body-background);
  --shadow-color: var(--settings-body-text-color-20);

  position: relative;
  outline: none;
  border: none;
  border-radius: .5rem;
  transition: all .2s linear;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
  background: var(--background);
  padding: .5rem 1rem;
  color: var(--text);
  text-decoration: none;
}
</style>
